import { useId, useState } from "react";
import { useZones } from "./useZones";
import { SettingsSection } from "./SettingsSection";
import { useTranslation } from "react-i18next";
import { ulid } from "ulid";
import { Button, Field, Input } from "../ui";
import { useDocSelector } from "../core/useDocSelector";
import { C } from "../core/schema";
import type { DocHandle } from "../sync/docManager";
import { setSetting } from "./settings";

interface Feed {
  id: string;
  name: string;
  url: string;
  color?: string;
}

const COLORS = ["--chart-1", "--chart-2", "--chart-3", "--chart-4", "--chart-5", "--chart-6"];

/**
 * Settings → Calendars (M10, level `standard`): the ICS feeds shown read-only in the planner. A private feed address is a secret: enter `env:DRANG_ICS_<NAME>` and put
 * the address into the server's `.env`.
 */
export function CalendarsSettings({ handle }: { handle: DocHandle | null }) {
  const { t } = useTranslation();
  const uid = useId();
  const ro = !handle || handle.readOnly;
  const saved = useDocSelector(handle, (doc) => {
    const s = doc.getMap(C.settings).toJSON() as { calendars?: Feed[]; timeZone?: string };
    return { feeds: Array.isArray(s.calendars) ? s.calendars : [], timeZone: s.timeZone ?? "" };
  });
  const feeds = saved?.feeds ?? [];
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const valid = /^(https:\/\/|webcal:\/\/|env:DRANG_ICS_[A-Z0-9_]+$)/.test(url.trim());
  const write = (next: Feed[]) => handle && !ro && setSetting(handle.doc, "calendars", next);

  return (
    <SettingsSection
      id="settings-calendars"
      intro={t("settings.intro.calendars")}
      title={t("calendars.title")}
      testId="settings-calendars"
      folded
    >
      <p className="text-[12.5px] text-neutral-500">{t("calendars.hint")}</p>
      {feeds.length > 0 && (
        <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
          {feeds.map((f) => (
            <li key={f.id} className="flex items-center gap-2 py-1.5" data-testid="calendar-feed">
              <span
                aria-hidden
                className="h-2.5 w-2.5 rounded-full"
                style={{ background: `var(${f.color ?? "--chart-other"})` }}
              />
              <span className="text-[13.5px]">{f.name}</span>
              <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-neutral-500">
                {f.url.startsWith("env:") ? f.url : f.url.replace(/^(\w+:\/\/[^/]+).*/, "$1/…")}
              </span>
              <Button
                size="sm"
                variant="secondary"
                disabled={ro}
                aria-label={t("calendars.removeLabel", { name: f.name })}
                onClick={() => write(feeds.filter((x) => x.id !== f.id))}
              >
                {t("calendars.remove")}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <Field htmlFor={`${uid}-name`} label={t("calendars.name")}>
          <Input
            id={`${uid}-name`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="calendar-name"
          />
        </Field>
        <Field htmlFor={`${uid}-url`} label={t("calendars.url")}>
          <Input
            id={`${uid}-url`}
            value={url}
            placeholder="env:DRANG_ICS_WORK"
            className="w-72 font-mono"
            onChange={(e) => setUrl(e.target.value)}
            data-testid="calendar-url"
          />
        </Field>
        <Button
          disabled={ro || !name.trim() || !valid}
          onClick={() => {
            write([
              ...feeds,
              { id: ulid(), name: name.trim(), url: url.trim(), color: COLORS[feeds.length % COLORS.length] },
            ]);
            setName("");
            setUrl("");
          }}
          data-testid="calendar-add"
        >
          {t("calendars.add")}
        </Button>
      </div>
      {url.trim() && !valid && <p className="text-[12px] text-neutral-500">{t("calendars.urlRule")}</p>}
    </SettingsSection>
  );
}

/**
 * Settings → Time zone (UI cleanup U5: out of *Calendars*): the workspace's zone, used for its days, repeats and
 * the calendar feeds. A person can set their own in *Notifications*; this one is their default.
 */
export function TimeZoneSettings({ handle }: { handle: DocHandle | null }) {
  const { t } = useTranslation();
  const uid = useId();
  const ro = !handle || handle.readOnly;
  const saved = useDocSelector(handle, (doc) => {
    const s = doc.getMap(C.settings).toJSON() as { timeZone?: string };
    return { timeZone: s.timeZone ?? "" };
  });
  const zones = useZones();
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <SettingsSection
      id="settings-timezone"
      intro={t("calendars.timeZoneHint")}
      title={t("calendars.timeZone")}
      testId="settings-timezone"
      folded
    >
      <Field htmlFor={`${uid}-tz`} label={t("calendars.timeZone")}>
        <Input
          id={`${uid}-tz`}
          list={`${uid}-zones`}
          value={saved?.timeZone ?? ""}
          placeholder={browserZone}
          disabled={ro}
          onChange={(e) => handle && !ro && setSetting(handle.doc, "timeZone", e.target.value.trim())}
          data-testid="calendars-timezone"
        />
        <datalist id={`${uid}-zones`}>
          {zones.map((z) => (
            <option key={z} value={z} />
          ))}
        </datalist>
      </Field>
    </SettingsSection>
  );
}
